import moment from "moment-timezone";
import { BorderButton } from "src/components/buttons/BorderButton";
import { Tag } from "src/components/forms/Tag";
import { MembershipOfferingDetail } from "src/components/items/MembershipOfferingDetail";
import { AddStudentToMembershipModal } from "src/components/modals/AddStudentToMembershipModal";
import { ManageRosterModal } from "src/components/modals/ManageRosterModal";
import MembershipModel from "src/models/MembershipModel";
import MembershipOfferingModel from "src/models/MembershipOfferingModel";
import { useSession } from "src/stores/Session";
import { useStore } from "src/stores/Store";
import { modals } from "src/utils/modals";
import tw from "src/utils/tw";
import { CapacityStatus, MembershipStatus } from "types/code-generator";
import { OfferingDetails } from "./OfferingDetails";
import { ItemContent } from "./item";
import ItemCard from "./item/ItemCard";

interface Props {
  offering: MembershipOfferingModel;
  isPreview?: boolean;
  isDetail?: boolean;
  loading?: boolean;
  hideAddButton?: boolean;
  isRequired?: boolean;
}

export default function MembershipOfferingItem(props: Props) {
  const store = useStore();
  const session = useSession();

  function selectOffering() {
    if (hasMembership) {
      const registration = store.user?.memberships.find(
        (m) =>
          m.offeringId === props.offering.id &&
          m.status !== MembershipStatus.Closed
      );
      modals.show(ManageRosterModal, {
        registration: new MembershipModel({
          ...registration,
          accountOwner: store.user,
          offering: props.offering,
        }),
        // handleCancel: () => {
        //   history.push(`/account/settings/enrollments`);
        // },
      });
    } else {
      modals.show(AddStudentToMembershipModal, {
        offering: props.offering,
      });
    }
  }

  function removeMembership(id) {
    session.removeRegistration(id);
  }

  function selectMembership(membership) {
    modals.show(AddStudentToMembershipModal, {
      membership,
      offering: membership.offering,
    });
  }

  function showStudentsTags() {
    const memberships = session.memberships.filter(
      (m) => m.offering?.id === props.offering.id
    );

    if (memberships.length === 0) {
      return null;
    }

    return (
      <div className="mt-4 flex gap-4">
        {memberships.map((s) => {
          return (
            <Tag
              key={s.id}
              closable={true}
              onClose={() => removeMembership(s.id)}
              onClick={() => selectMembership(s)}
            >
              {s.roster.map((r) => r.firstName).join(", ")}
            </Tag>
          );
        })}
      </div>
    );
  }

  let offeringDetail;

  if (props.isDetail) {
    offeringDetail = (
      <MembershipOfferingDetail
        offering={props.offering}
        isDetail={props.isDetail}
      />
    );
  }

  let addBtnText = `Add Membership`;
  const hasMembership = Boolean(
    store.user?.memberships.find(
      (m) =>
        m.offeringId === props.offering.id &&
        m.status !== MembershipStatus.Closed
    ) &&
      !(
        store.user.isAdminOrAbove &&
        session.accountOwner &&
        session.accountOwner.id !== store.user.id
      )
  );

  // console.log("hasMembership", hasMembership);

  const membershipInCart = Boolean(
    session.memberships.find((m) => m.offering.id === props.offering.id)
  );
  const registrationOpen =
    props.offering.registrationOpen && moment(props.offering.registrationOpen);
  const registrationClose =
    props.offering.registrationClose &&
    moment(props.offering.registrationClose);
  const today = moment();
  const registrationStatus = props.offering.capacity;
  const waitlist = props.offering.memberships.filter(
    (m) => m.status === MembershipStatus.Waitlist
  );

  if (hasMembership) {
    addBtnText = "Manage Members";
  }

  if (membershipInCart) {
    addBtnText = "In Cart";
  }

  if (
    (registrationClose && today.isAfter(registrationClose)) ||
    !registrationOpen
  ) {
    addBtnText = `Registration Closed`;
  }

  if (
    registrationStatus === CapacityStatus.Waitlist ||
    (registrationStatus === CapacityStatus.WaitlistAvailable &&
      waitlist.length > 0 &&
      waitlist.length < props.offering.waitlistCapacity)
  ) {
    addBtnText = `Add ${props.offering.participantType} to Waitlist`;
  }

  // console.log("membershipInCart", membershipInCart);

  let addButton = (
    <BorderButton
      type="submit"
      onClick={selectOffering}
      isPreview={props.isPreview}
      disabled={
        membershipInCart ||
        !registrationOpen ||
        (registrationOpen && registrationOpen.isAfter(today)) ||
        (registrationClose && registrationClose.isBefore(today))
      }
    >
      {addBtnText}
    </BorderButton>
  );

  if (store.isAdminArea && !props.isPreview) {
    addButton = (
      <BorderButton type="submit" onClick={selectOffering}>
        {addBtnText}
      </BorderButton>
    );
  }

  if (props.hideAddButton) {
    addButton = null;
  }

  // console.log("roster", roster);

  return (
    <ItemCard
      loading={props.loading === undefined ? true : props.loading}
      className={tw(props.isDetail && "mb-0 border-b-0")}
    >
      <ItemContent>
        {!store.isAdminArea && props.isRequired && (
          <div className="mb-3 flex items-center gap-2 rounded bg-lightGray px-3 py-2 text-sm">
            <i className="far fa-certificate text-empty" />
            <div className="step-subhead">
              offerings listed may require this membership
            </div>
          </div>
        )}
        <OfferingDetails
          offering={props.offering}
          showPrice
          linkToOffering
          isPreview={props.isPreview}
        />
      </ItemContent>

      {addButton && <div className="pt-2">{addButton}</div>}

      {!props.isPreview && showStudentsTags()}

      {offeringDetail}
    </ItemCard>
  );
}
